import { useEffect, useRef } from "react";

/*
  KonfetiCanvas
  ─────────────
  • requestAnimationFrame tabanlı, kütüphane bağımlılığı yok
  • ~3.2s sonra otomatik durur, canvas DOM'dan kaldırılır
  • Web Audio API ile yumuşak kutlama çan sesi (dosya gerekmez)
  • active=false geçince hemen temizlenir
*/

const COLORS = [
  "#6366f1", "#818cf8", "#a78bfa",  // indigo/mor — tema uyumlu
  "#f59e0b", "#fbbf24",             // altın sarısı
  "#ec4899", "#f472b6",             // pembe
  "#10b981", "#34d399",             // yeşil
  "#fff",                           // beyaz parıltı
];

function createParticle(W, H) {
  const size  = Math.random() * 7 + 4;
  const shape = Math.random() < 0.55 ? "rect" : "circle";
  return {
    x:     Math.random() * W,
    y:     -size - Math.random() * H * 0.3,
    vx:    (Math.random() - 0.5) * 1.8,
    vy:    Math.random() * 2.0 + 0.9,
    rot:   Math.random() * Math.PI * 2,
    vrot:  (Math.random() - 0.5) * 0.18,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size,
    shape,
    opacity: 1,
  };
}

function playCelebrationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const notes = [
      { freq: 523.25, t: 0.00 },  // C5
      { freq: 659.25, t: 0.10 },  // E5
      { freq: 783.99, t: 0.20 },  // G5
      { freq: 1046.5, t: 0.32 },  // C6 — yüksek parlak ton
    ];

    notes.forEach(({ freq, t }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type      = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + t);

      gain.gain.setValueAtTime(0,    ctx.currentTime + t);
      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.55);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.6);
    });

    // Hafif shimmer — üstte parlak glitter hissi
    const shimmer   = ctx.createOscillator();
    const shimGain  = ctx.createGain();
    shimmer.type    = "triangle";
    shimmer.frequency.setValueAtTime(2093, ctx.currentTime + 0.28);
    shimGain.gain.setValueAtTime(0,      ctx.currentTime + 0.28);
    shimGain.gain.linearRampToValueAtTime(0.07, ctx.currentTime + 0.34);
    shimGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    shimmer.connect(shimGain);
    shimGain.connect(ctx.destination);
    shimmer.start(ctx.currentTime + 0.28);
    shimmer.stop(ctx.currentTime + 0.95);

    setTimeout(() => { try { ctx.close(); } catch {} }, 2000);
  } catch {
    // Ses desteklenmiyor — sessizce geç
  }
}

export default function KonfetiCanvas({ active, onDone }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const startRef  = useRef(null);

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx    = canvas.getContext("2d");
    const W      = canvas.width  = window.innerWidth;
    const H      = canvas.height = window.innerHeight;
    const TOTAL  = Math.min(Math.floor(W * 0.28), 120);
    const DURATION = 4200; // ms — animasyon süresi
    const FADE_AT  = 3200; // ms — bu noktadan itibaren solmaya başlar

    const particles = Array.from({ length: TOTAL }, () => createParticle(W, H));

    playCelebrationSound();

    startRef.current = performance.now();

    function draw(now) {
      const elapsed = now - startRef.current;
      if (elapsed > DURATION) {
        ctx.clearRect(0, 0, W, H);
        if (onDone) onDone();
        return;
      }

      const globalAlpha = elapsed > FADE_AT
        ? 1 - (elapsed - FADE_AT) / (DURATION - FADE_AT)
        : 1;

      ctx.clearRect(0, 0, W, H);

      particles.forEach((p) => {
        p.x   += p.vx;
        p.y   += p.vy;
        p.rot += p.vrot;
        p.vy  += 0.03; // hafif yerçekimi

        // Ekrandan çıkan parçacığı sıfırla — ilk 2.4s içinde
        if (p.y > H + 10 && elapsed < 2400) {
          p.x  = Math.random() * W;
          p.y  = -p.size - Math.random() * 40;
          p.vy = Math.random() * 2.0 + 0.9;
          p.vx = (Math.random() - 0.5) * 1.8;
        }

        ctx.save();
        ctx.globalAlpha = globalAlpha * p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;

        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size * 0.3, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size * 0.45, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });

      rafRef.current = requestAnimationFrame(draw);
    }

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 99998,   // popup (9999) altında ama her şeyin üstünde
      }}
      aria-hidden="true"
    />
  );
}
